import { useEffect, useMemo, useState } from 'react';
import Button from '@/components/Button';
import { TypographyMuted, TypographyXS } from '@/components/ui/typography';
import { Resale } from '@/types/Resale';
import { UserPermissions } from '@/types/User';
import {
  Building2,
  CircleCheckBig,
  OctagonX,
  Plus,
  Trash2,
  User,
  UserCheck,
  Loader2,
  Search,
  ChevronsUpDown,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from '@/api/business';
import {
  createUserPermissions,
  deleteUserPermissions,
} from '@/api/business/user';
import { UserPermissionsKey } from '@/queries/useUserPermissionsQuery';
import { useEmployeeQuery } from '@/queries/useEmployeeQuery';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

interface Props {
  resale: Resale;
  dataUserPermissions: UserPermissions[];
  idUser: number;
  userProfile?: string;
}

const CardResale = ({
  resale,
  dataUserPermissions,
  idUser,
  userProfile,
}: Props) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isRepresentante = userProfile === 'REPRESENTANTE';
  const resaleId = resale.NK_REVENDA || resale.SK_REVENDA;

  const { data: employeesData, isLoading: isLoadingEmployees } =
    useEmployeeQuery(resaleId, isRepresentante);

  const resalePermissions = useMemo(() => {
    return (dataUserPermissions || []).filter(
      (p) =>
        (p.NK_REVENDA && p.NK_REVENDA === resaleId) ||
        p.SK_REVENDA === resaleId,
    );
  }, [dataUserPermissions, resaleId]);

  const haveAccess = resalePermissions.length > 0;
  const permission = resalePermissions[0];

  const linkedEmployees = useMemo(() => {
    const map = new Map<string, string>();
    resalePermissions.forEach((p) => {
      if (p.SK_FUNCIONARIO) {
        map.set(
          String(p.SK_FUNCIONARIO),
          p.NM_FUNCIONARIO || `Funcionário #${p.SK_FUNCIONARIO}`,
        );
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({
      SK_FUNCIONARIO: id,
      NM_FUNCIONARIO: name,
    }));
  }, [resalePermissions]);

  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [searchEmployeeText, setSearchEmployeeText] = useState<string>('');
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  useEffect(() => {
    const currentEmpIds = resalePermissions
      .filter((p) => Boolean(p.SK_FUNCIONARIO))
      .map((p) => String(p.SK_FUNCIONARIO));
    setSelectedEmployees(currentEmpIds);
  }, [resalePermissions]);

  const filteredEmployees = useMemo(() => {
    if (!employeesData) return [];
    if (!searchEmployeeText.trim()) return employeesData;
    const searchLower = searchEmployeeText.toLowerCase();
    return employeesData.filter(
      (emp) =>
        emp.NM_FUNCIONARIO.toLowerCase().includes(searchLower) ||
        String(emp.NK_FUNCIONARIO || emp.SK_FUNCIONARIO)
          .toLowerCase()
          .includes(searchLower),
    );
  }, [employeesData, searchEmployeeText]);

  const handleToggleEmployee = (skFuncionario: string) => {
    setSelectedEmployees((prev) =>
      prev.includes(skFuncionario)
        ? prev.filter((id) => id !== skFuncionario)
        : [...prev, skFuncionario],
    );
  };

  const handleSelectAllEmployees = () => {
    if (!filteredEmployees) return;
    const idsToAdd = filteredEmployees.map((emp) => String(emp.SK_FUNCIONARIO));
    setSelectedEmployees((prev) => Array.from(new Set([...prev, ...idsToAdd])));
  };

  const handleClearEmployees = () => {
    setSelectedEmployees([]);
  };

  const { mutate: mutateAddPermission, isPending: mutateAddPermissionLoading } =
    useMutation({
      mutationFn: createUserPermissions,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: UserPermissionsKey.all });
        toast({
          title: 'Acesso concedido',
          description: `Permissões da revenda ${resale.DS_REVENDA} atualizadas com sucesso.`,
        });
      },
      onError: (err) => {
        let errorMessage =
          'Não foi possível adicionar o acesso. Tente novamente mais tarde.';
        if (isAxiosError(err)) {
          errorMessage = err.response?.data.message || errorMessage;
        }
        toast({
          title: 'Erro ao conceder acesso',
          description: errorMessage,
          variant: 'destructive',
        });
      },
    });

  const {
    mutate: mutateDeletePermission,
    isPending: mutateDeletePermissionLoading,
  } = useMutation({
    mutationFn: deleteUserPermissions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UserPermissionsKey.all });
      toast({
        title: 'Acesso removido',
        description: `Acesso à revenda ${resale.DS_REVENDA} removido com sucesso.`,
      });
    },
    onError: (err) => {
      let errorMessage =
        'Não foi possível remover o acesso. Tente novamente mais tarde.';
      if (isAxiosError(err)) {
        errorMessage = err.response?.data.message || errorMessage;
      }
      toast({
        title: 'Erro ao remover acesso',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const handleAddPermission = () => {
    mutateAddPermission({
      SK_REVENDA: resaleId,
      SK_USUARIO: idUser,
      SK_FUNCIONARIOS: isRepresentante ? selectedEmployees : undefined,
    });
  };

  const handleDeletePermission = () => {
    mutateDeletePermission({
      SK_DIM_ACESSO_USUARIO_REVENDA: permission?.SK_DIM_ACESSO_USUARIO_REVENDA,
      SK_REVENDA: resaleId,
      SK_USUARIO: idUser,
    });
  };

  return (
    <div
      className="flex flex-col justify-between gap-3 p-4 transition-shadow bg-white border border-gray-200 shadow-sm rounded-xl hover:shadow-md"
      key={resaleId}
    >
      <div>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <TypographyXS className="text-base font-semibold text-gray-900">
                {resale.DS_REVENDA}
              </TypographyXS>
              {resale.DD_UF && (
                <TypographyMuted className="text-xs">
                  UF: {resale.DD_UF}
                </TypographyMuted>
              )}
            </div>
          </div>
        </div>

        {resale.DS_PROPRIETARIO && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-2">
            <User className="w-3.5 h-3.5 text-gray-400" />
            <span>
              Proprietário: <strong>{resale.DS_PROPRIETARIO}</strong>
            </span>
          </div>
        )}

        {isRepresentante && (
          <div className="mt-3 pt-2 border-t border-gray-100 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 text-xs font-medium text-gray-700">
                <UserCheck className="w-3.5 h-3.5 text-primary" /> Vendedores /
                Funcionários:
              </span>
              {isLoadingEmployees && (
                <span className="text-[10px] text-gray-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Carregando...
                </span>
              )}
            </div>

            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-between w-full px-3 text-xs text-gray-700 transition-colors border border-gray-200 rounded-md h-9 bg-gray-50 hover:bg-gray-100"
                >
                  <span className="truncate">Selecione os vendedores...</span>
                  <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400 shrink-0 ml-2" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="z-50 p-0 bg-white border border-gray-200 rounded-lg shadow-lg w-80"
                align="start"
              >
                <div className="p-2 border-b border-gray-100">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
                    <Input
                      placeholder="Buscar por nome ou código..."
                      value={searchEmployeeText}
                      onChange={(e) => setSearchEmployeeText(e.target.value)}
                      className="h-8 pl-8 text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-1 text-[11px]">
                    <button
                      type="button"
                      onClick={handleSelectAllEmployees}
                      className="font-medium text-primary hover:underline"
                    >
                      Selecionar todos ({filteredEmployees.length})
                    </button>
                    <button
                      type="button"
                      onClick={handleClearEmployees}
                      className="font-medium text-red-500 hover:underline"
                    >
                      Limpar seleção
                    </button>
                  </div>
                </div>

                <div className="max-h-56 overflow-y-auto p-1 space-y-0.5">
                  {filteredEmployees && filteredEmployees.length > 0 ? (
                    filteredEmployees.map((emp) => {
                      const empIdStr = String(emp.SK_FUNCIONARIO);
                      const isChecked = selectedEmployees.includes(empIdStr);
                      return (
                        <div
                          key={emp.SK_FUNCIONARIO}
                          onClick={() => handleToggleEmployee(empIdStr)}
                          className="flex items-center gap-2.5 px-2.5 py-1.5 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() =>
                              handleToggleEmployee(empIdStr)
                            }
                          />
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-xs font-medium text-gray-800 truncate">
                              {emp.NM_FUNCIONARIO}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              Código: {emp.NK_FUNCIONARIO || emp.SK_FUNCIONARIO}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="py-6 text-xs text-center text-gray-400">
                      Nenhum vendedor encontrado
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {haveAccess && linkedEmployees.length > 0 && (
          <div className="mt-2.5 p-2 rounded-lg bg-emerald-50/70 border border-emerald-200">
            <div className="flex items-center justify-between mb-1.5">
              <span className="flex items-center gap-1 text-xs font-semibold text-emerald-800">
                <UserCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                Vendedor(es) vinculado(s) ({linkedEmployees.length}):
              </span>
            </div>
            <div className="flex flex-wrap gap-1 overflow-y-auto max-h-20">
              {linkedEmployees.map((emp) => (
                <span
                  key={emp.SK_FUNCIONARIO}
                  className="text-[11px] bg-white text-emerald-900 border border-emerald-200 px-2 py-0.5 rounded font-medium shadow-2xs"
                >
                  {emp.NM_FUNCIONARIO}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 mt-1 border-t border-gray-100">
        {haveAccess ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CircleCheckBig size={15} />
            <span>Com acesso</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            <OctagonX size={15} />
            <span>Sem acesso</span>
          </div>
        )}

        {haveAccess ? (
          <div className="flex items-center gap-2">
            {isRepresentante && (
              <Button
                className="flex gap-1.5 bg-primary/90 hover:bg-primary text-white text-xs"
                size={'sm'}
                isLoading={mutateAddPermissionLoading}
                onClick={handleAddPermission}
              >
                Salvar Vendedores
              </Button>
            )}
            <Button
              className="flex gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs"
              size={'sm'}
              isLoading={mutateDeletePermissionLoading}
              onClick={handleDeletePermission}
            >
              <Trash2 size={15} />
              Remover Acesso
            </Button>
          </div>
        ) : (
          <Button
            className="flex gap-1.5 bg-primary hover:bg-primary/90 text-xs"
            size={'sm'}
            isLoading={mutateAddPermissionLoading}
            onClick={handleAddPermission}
          >
            <Plus size={15} />
            Conceder Acesso
          </Button>
        )}
      </div>
    </div>
  );
};

export default CardResale;
